/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Home,
} from "lucide-react";
import { apiFetch } from "../auth/apiClient";
import { propertiesApi, startIciciPayment } from "./api";
import PhotosEditor from "./PhotosEditor";
import "./CreateListing.css";

const STEPS = [
  { id: 0, label: "Basic Info" },
  { id: 1, label: "Photos" },
  { id: 2, label: "Amenities" },
  { id: 3, label: "Pricing & Publish" },
];

const AMENITY_CATEGORIES = [
  {
    key: "bedroom",
    label: "Bedroom",
    items: ["Beds", "Mattresses", "Wardrobes", "AC", "Curtains"],
  },
  {
    key: "living",
    label: "Living Room",
    items: ["Sofa", "TV", "TV Unit", "Center Table", "Curtains"],
  },
  {
    key: "kitchen",
    label: "Kitchen",
    items: [
      "Modular Kitchen",
      "Chimney",
      "Refrigerator",
      "Microwave",
      "Water Purifier",
      "Gas Stove",
    ],
  },
  {
    key: "bathroom",
    label: "Bathroom",
    items: ["Geyser", "Exhaust Fan"],
  },
  {
    key: "other",
    label: "Other Amenities",
    items: [
      "Washing Machine",
      "Dining Table",
      "Study Table",
      "Power Backup",
      "Private Garden",
      "Terrace Access",
      "Servant Room",
      "Store Room",
      "Balcony",
    ],
  },
];

const EMPTY = {
  villa_id: "",
  listing_type: "RENT",
  bedrooms: 4,
  bathrooms: 4,
  parking: 2,
  built_up_area_sqft: "",
  facing: "",
  furnishing: "SEMI",
  description: "",
  property_age_years: "",
  floor_label: "",
  monthly_rent_inr: "",
  monthly_maintenance_inr: "",
  security_deposit_months: 2,
  available_from: "",
  min_lease_months: 11,
  expected_price_inr: "",
  possession: "READY",
  price_negotiable: false,
  vegetarian_only: false,
  pets_allowed: false,
  non_smokers_only: false,
  show_phone: true,
  allow_calls: true,
  inquiries_via_email: true,
  inquiries_via_whatsapp: true,
  visibility: "COMMUNITY_ONLY",
};

export default function CreateListing() {
  const { villaId: prefilledVillaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // HashRouter puts the query AFTER the hash path — pull from the
  // location.search, which CRA exposes correctly via useLocation.
  const queryParams = new URLSearchParams(location.search);
  // The Villa page launches "Put up for rent/sale" with these set,
  // so the form lands pre-filled and the user only needs to fill the
  // remaining fields.
  const queryVillaId = queryParams.get("villa_id") || "";
  const queryListingType = queryParams.get("type") === "SALE" ? "SALE" : null;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    ...EMPTY,
    villa_id: queryVillaId || prefilledVillaId || "",
    listing_type: queryListingType || EMPTY.listing_type,
  });
  const [amenities, setAmenities] = useState({});
  const [listingId, setListingId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [villas, setVillas] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");

  // Load villas the current user can list (own/co-owner approved villas).
  useEffect(() => {
    apiFetch("/api/users/me", { authRequired: true })
      .then((me) => {
        const owned = (me.memberships || []).filter(
          (m) =>
            (m.role === "PRIMARY_OWNER" || m.role === "SHARED_OWNER") &&
            m.approval_status === "APPROVED"
        );
        setVillas(owned);
        if (!prefilledVillaId && owned.length > 0) {
          setForm((f) => ({ ...f, villa_id: owned[0].villa.id }));
        }
      })
      .catch(() => {});
  }, [prefilledVillaId]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleAmenity = (cat, label) => {
    setAmenities((m) => {
      const items = m[cat] || [];
      const has = items.includes(label);
      return {
        ...m,
        [cat]: has ? items.filter((x) => x !== label) : [...items, label],
      };
    });
  };

  const validateStep = (which) => {
    if (which === 0) {
      if (!form.villa_id) return "Pick a villa";
      if (!form.bedrooms) return "Set bedrooms";
      if (!form.built_up_area_sqft) return "Set built-up area";
      if (!form.description || form.description.trim().length < 20)
        return "Description should be at least 20 characters";
    }
    if (which === 3) {
      if (form.listing_type === "RENT" && !form.monthly_rent_inr)
        return "Set monthly rent";
      if (form.listing_type === "SALE" && !form.expected_price_inr)
        return "Set expected price";
    }
    return "";
  };

  const goNext = async () => {
    setError("");
    const v = validateStep(step);
    if (v) {
      setError(v);
      return;
    }
    if (step === 0 && !listingId) {
      // Create the DRAFT now so the photo upload step has an id to attach to.
      setSubmitting(true);
      try {
        const created = await propertiesApi.create({
          villa_id: form.villa_id,
          listing_type: form.listing_type,
          bedrooms: Number(form.bedrooms),
          bathrooms: Number(form.bathrooms),
          parking: Number(form.parking || 0),
          built_up_area_sqft: Number(form.built_up_area_sqft),
          facing: form.facing || null,
          furnishing: form.furnishing,
          description: form.description.trim(),
          property_age_years: form.property_age_years
            ? Number(form.property_age_years)
            : null,
          floor_label: form.floor_label || null,
          // Pricing fields can also be sent so PATCH later doesn't need them
          monthly_rent_inr: form.monthly_rent_inr
            ? Number(form.monthly_rent_inr)
            : null,
          monthly_maintenance_inr: form.monthly_maintenance_inr
            ? Number(form.monthly_maintenance_inr)
            : null,
          expected_price_inr: form.expected_price_inr
            ? Number(form.expected_price_inr)
            : null,
        });
        setListingId(created.id);
      } catch (err) {
        setError(err.message || "Could not save");
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    } else if (listingId) {
      // Persist intermediate edits without blocking the wizard nav.
      try {
        const patchBody = {
          bedrooms: Number(form.bedrooms),
          bathrooms: Number(form.bathrooms),
          parking: Number(form.parking || 0),
          built_up_area_sqft: Number(form.built_up_area_sqft),
          facing: form.facing || null,
          furnishing: form.furnishing,
          description: form.description.trim(),
          property_age_years: form.property_age_years
            ? Number(form.property_age_years)
            : null,
          floor_label: form.floor_label || null,
        };
        if (step === 2) patchBody.amenities = amenities;
        await propertiesApi.patch(listingId, patchBody);
      } catch (_) {}
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const publish = async () => {
    setError("");
    const v = validateStep(3);
    if (v) {
      setError(v);
      return;
    }
    setPublishing(true);
    try {
      // Final patch with pricing + preferences + visibility.
      await propertiesApi.patch(listingId, {
        monthly_rent_inr: form.monthly_rent_inr
          ? Number(form.monthly_rent_inr)
          : null,
        monthly_maintenance_inr: form.monthly_maintenance_inr
          ? Number(form.monthly_maintenance_inr)
          : null,
        security_deposit_months: form.security_deposit_months
          ? Number(form.security_deposit_months)
          : null,
        available_from: form.available_from || null,
        min_lease_months: form.min_lease_months
          ? Number(form.min_lease_months)
          : null,
        expected_price_inr: form.expected_price_inr
          ? Number(form.expected_price_inr)
          : null,
        possession: form.possession || null,
        price_negotiable: !!form.price_negotiable,
        vegetarian_only: !!form.vegetarian_only,
        pets_allowed: !!form.pets_allowed,
        non_smokers_only: !!form.non_smokers_only,
        show_phone: !!form.show_phone,
        allow_calls: !!form.allow_calls,
        inquiries_via_email: !!form.inquiries_via_email,
        inquiries_via_whatsapp: !!form.inquiries_via_whatsapp,
        visibility: form.visibility,
        amenities,
      });
      const res = await propertiesApi.publish(listingId);
      // Publishing now collects a ₹100 fee via ICICI PgPay. The backend
      // returns an encrypted form to submit; this navigates away to the
      // ICICI hosted page and back to /properties/me?payment=…
      if (res && res.payment) {
        startIciciPayment(res.payment);
        return; // page is redirecting; keep the button disabled
      }
      navigate(`/properties/${listingId}`);
    } catch (err) {
      setError(err.message || "Could not publish");
      setPublishing(false);
    }
  };

  return (
    <div className="vsp-cl-shell">
      <button className="vsp-cl-back" onClick={() => navigate("/properties")}>
        <ArrowLeft size={14} /> Back to properties
      </button>

      <h1>List your villa</h1>
      <p className="vsp-cl-sub">
        Free to list. Goes live the moment you hit Publish. Auto-expires
        after 30 days; renew anytime with one click.
      </p>

      <div className="vsp-cl-progress">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={`vsp-cl-step ${
              step === s.id ? "active" : step > s.id ? "done" : ""
            }`}
          >
            <div className="vsp-cl-step-circle">
              {step > s.id ? <Check size={14} /> : s.id + 1}
            </div>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="vsp-cl-card">
        {step === 0 && (
          <BasicInfo
            form={form}
            upd={upd}
            villas={villas}
          />
        )}
        {step === 1 && (
          <PhotosEditor
            listingId={listingId}
            initial={photos}
            onChange={setPhotos}
          />
        )}
        {step === 2 && (
          <AmenitiesStep
            amenities={amenities}
            onToggle={toggleAmenity}
          />
        )}
        {step === 3 && (
          <PricingStep
            form={form}
            upd={upd}
          />
        )}

        {error && <div className="vsp-cl-error">{error}</div>}

        <div className="vsp-cl-actions">
          {step > 0 && (
            <button
              type="button"
              className="vsp-cl-btn-secondary"
              onClick={goBack}
              disabled={submitting || publishing}
            >
              <ArrowLeft size={14} /> Previous
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="vsp-cl-btn-primary"
              onClick={goNext}
              disabled={submitting}
            >
              {submitting
                ? "Saving…"
                : `Next: ${STEPS[step + 1].label}`}
              <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              className="vsp-cl-btn-primary"
              onClick={publish}
              disabled={publishing || photos.length === 0}
            >
              {publishing ? "Publishing…" : "Publish listing"}
              <Check size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BasicInfo({ form, upd, villas }) {
  return (
    <div className="vsp-cl-grid">
      <Field label="Villa *" wide>
        <select
          value={form.villa_id}
          onChange={(e) => upd({ villa_id: e.target.value })}
        >
          <option value="">— Pick a villa —</option>
          {villas.map((m) => (
            <option key={m.villa.id} value={m.villa.id}>
              {m.villa.villa_number}
            </option>
          ))}
        </select>
        <p className="vsp-cl-helper">
          Only villas where you're an approved owner are listed.
        </p>
      </Field>
      <Field label="Listing type *">
        <div className="vsp-cl-pills">
          {["RENT", "SALE"].map((t) => (
            <button
              key={t}
              type="button"
              className={`vsp-cl-pill ${
                form.listing_type === t ? "active" : ""
              }`}
              onClick={() => upd({ listing_type: t })}
            >
              {t === "RENT" ? "For Rent" : "For Sale"}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Furnishing *">
        <div className="vsp-cl-pills">
          {[
            ["FURNISHED", "Furnished"],
            ["SEMI", "Semi"],
            ["UNFURNISHED", "Unfurnished"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={`vsp-cl-pill ${
                form.furnishing === v ? "active" : ""
              }`}
              onClick={() => upd({ furnishing: v })}
            >
              {label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Bedrooms *">
        <div className="vsp-cl-pills">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={`vsp-cl-pill ${
                Number(form.bedrooms) === n ? "active" : ""
              }`}
              onClick={() => upd({ bedrooms: n })}
            >
              {n === 5 ? "5+" : n}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Bathrooms *">
        <input
          type="number"
          min={1}
          max={20}
          value={form.bathrooms}
          onChange={(e) => upd({ bathrooms: e.target.value })}
        />
      </Field>
      <Field label="Parking">
        <input
          type="number"
          min={0}
          max={20}
          value={form.parking}
          onChange={(e) => upd({ parking: e.target.value })}
        />
      </Field>
      <Field label="Built-up area (sqft) *">
        <input
          type="number"
          min={100}
          value={form.built_up_area_sqft}
          onChange={(e) => upd({ built_up_area_sqft: e.target.value })}
          placeholder="2400"
        />
      </Field>
      <Field label="Facing">
        <select
          value={form.facing}
          onChange={(e) => upd({ facing: e.target.value })}
        >
          <option value="">— optional —</option>
          <option value="EAST">East</option>
          <option value="WEST">West</option>
          <option value="NORTH">North</option>
          <option value="SOUTH">South</option>
        </select>
      </Field>
      <Field label="Property age (years)">
        <input
          type="number"
          min={0}
          value={form.property_age_years}
          onChange={(e) => upd({ property_age_years: e.target.value })}
          placeholder="optional"
        />
      </Field>
      <Field label="Description *" wide>
        <textarea
          rows={5}
          value={form.description}
          onChange={(e) => upd({ description: e.target.value })}
          placeholder="Highlight unique features, recent renovations, neighbourhood…"
          maxLength={2000}
        />
        <p className="vsp-cl-helper">
          {form.description.length} / 2000 — min 20 chars
        </p>
      </Field>
    </div>
  );
}

function AmenitiesStep({ amenities, onToggle }) {
  return (
    <div className="vsp-cl-amen">
      {AMENITY_CATEGORIES.map((cat) => (
        <div key={cat.key} className="vsp-cl-amen-cat">
          <h4>{cat.label}</h4>
          <div className="vsp-cl-amen-items">
            {cat.items.map((item) => {
              const checked = (amenities[cat.key] || []).includes(item);
              return (
                <label
                  key={item}
                  className={`vsp-cl-amen-item ${checked ? "checked" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(cat.key, item)}
                  />
                  <span>{item}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function PricingStep({ form, upd }) {
  return (
    <div className="vsp-cl-grid">
      {form.listing_type === "RENT" ? (
        <>
          <Field label="Monthly rent (₹) *">
            <input
              type="number"
              min={0}
              value={form.monthly_rent_inr}
              onChange={(e) => upd({ monthly_rent_inr: e.target.value })}
              placeholder="45000"
            />
          </Field>
          <Field label="Monthly maintenance (₹)">
            <input
              type="number"
              min={0}
              value={form.monthly_maintenance_inr}
              onChange={(e) =>
                upd({ monthly_maintenance_inr: e.target.value })
              }
              placeholder="5000 (society charges)"
            />
            <p className="vsp-cl-helper">
              Society maintenance the tenant pays on top of rent.
            </p>
          </Field>
          <Field label="Security deposit (months)">
            <input
              type="number"
              min={0}
              max={12}
              value={form.security_deposit_months}
              onChange={(e) =>
                upd({ security_deposit_months: e.target.value })
              }
            />
          </Field>
          <Field label="Available from">
            <input
              type="date"
              value={form.available_from}
              onChange={(e) => upd({ available_from: e.target.value })}
            />
          </Field>
          <Field label="Minimum lease (months)">
            <select
              value={form.min_lease_months}
              onChange={(e) => upd({ min_lease_months: e.target.value })}
            >
              <option value={6}>6 months</option>
              <option value={11}>11 months</option>
              <option value={12}>1 year</option>
              <option value={24}>2 years</option>
            </select>
          </Field>
        </>
      ) : (
        <>
          <Field label="Expected price (₹) *">
            <input
              type="number"
              min={0}
              value={form.expected_price_inr}
              onChange={(e) => upd({ expected_price_inr: e.target.value })}
              placeholder="15000000"
            />
          </Field>
          <Field label="Possession">
            <select
              value={form.possession}
              onChange={(e) => upd({ possession: e.target.value })}
            >
              <option value="READY">Ready to Move</option>
              <option value="WITHIN_3_MONTHS">Within 3 months</option>
              <option value="WITHIN_6_MONTHS">Within 6 months</option>
            </select>
          </Field>
          <Field label="Negotiable?">
            <Toggle
              checked={form.price_negotiable}
              onChange={(v) => upd({ price_negotiable: v })}
              label="Price is negotiable"
            />
          </Field>
        </>
      )}

      <Field label="Preferences" wide>
        <div className="vsp-cl-pref">
          <Toggle
            checked={form.vegetarian_only}
            onChange={(v) => upd({ vegetarian_only: v })}
            label="Vegetarians only"
          />
          <Toggle
            checked={form.pets_allowed}
            onChange={(v) => upd({ pets_allowed: v })}
            label="Pets allowed"
          />
          <Toggle
            checked={form.non_smokers_only}
            onChange={(v) => upd({ non_smokers_only: v })}
            label="Non-smokers only"
          />
        </div>
      </Field>

      <Field label="Visibility *" wide>
        <div className="vsp-cl-pills">
          <button
            type="button"
            className={`vsp-cl-pill ${
              form.visibility === "COMMUNITY_ONLY" ? "active" : ""
            }`}
            onClick={() => upd({ visibility: "COMMUNITY_ONLY" })}
          >
            Community only
          </button>
          <button
            type="button"
            className={`vsp-cl-pill ${
              form.visibility === "PUBLIC" ? "active" : ""
            }`}
            onClick={() => upd({ visibility: "PUBLIC" })}
          >
            Public (when external browse goes live)
          </button>
          <button
            type="button"
            className={`vsp-cl-pill ${
              form.visibility === "HIDDEN" ? "active" : ""
            }`}
            onClick={() => upd({ visibility: "HIDDEN" })}
          >
            Hidden (draft)
          </button>
        </div>
      </Field>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={`vsp-cl-field ${wide ? "wide" : ""}`}>
      <label>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="vsp-cl-toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}
